<script lang="ts" setup>
import type { CSSProperties, PropType} from 'vue';
import { onMounted, ref, onUnmounted } from 'vue';
import type { ICellRendererParams } from 'ag-grid-community';

type ExtendedParams = ICellRendererParams & {
    id: string;
    title: (params: ICellRendererParams) => string;
    icon: (params: ICellRendererParams) => string;
    wrap: (params: ICellRendererParams) => boolean;
    tooltip: (params: ICellRendererParams) => boolean;
    readonly: ((params: ICellRendererParams) => boolean) | boolean;
    textStyle: ((params: ICellRendererParams) => CSSProperties) | CSSProperties;
};

const props = defineProps({
    params: {
        type: Object as PropType<ExtendedParams>,
        required: true
    }
});

const title = ref<string>('status');
const icon = ref<string>('check');
const readonly = ref<boolean>(false);
const wrap = ref(false);
const textStyle = ref<CSSProperties>({});
const tooltip = ref(true);

const isEditing = ref(false);

onMounted(() => {
    props.params.api.addEventListener('cellEditingStarted', event => {
        if (event.node.id === props.params.node.id && event.colDef?.field === props.params.colDef?.field) {
            isEditing.value = event.node.id === props.params.node.id;
        }
    });
    props.params.api.addEventListener('cellEditingStopped', () => {
        isEditing.value = false;
    });
    title.value = typeof props.params.title === 'function' ? props.params.title(props.params) : props.params.title;
    icon.value = typeof props.params.icon === 'function' ? props.params.icon(props.params) : props.params.icon;
    readonly.value = typeof props.params.readonly === 'function' ? props.params.readonly(props.params) : props.params.readonly;
    wrap.value = typeof props.params.wrap === 'function' ? props.params.wrap(props.params) : props.params.wrap;
    textStyle.value = typeof props.params.textStyle === 'function' ? props.params.textStyle(props.params) : props.params.textStyle;

    const tooltipPropVal = typeof props.params.tooltip === 'function' ? props.params.tooltip(props.params) : props.params.tooltip;
    tooltip.value = tooltipPropVal ?? true;
});

onUnmounted(() => {
    if (props.params.api.isDestroyed()) return;
    props.params.api.removeEventListener('cellEditingStarted', () => {});
    props.params.api.removeEventListener('cellEditingStopped', () => {});
});

</script>

<template>
    <div :class="['sy-edit-field', { 'sy-default': readonly, 'editing': isEditing }]">
        <span
            v-if="tooltip"
            v-tooltip="title"
            :style="textStyle"
            :class="[wrap ? 'sy-pre-wrap sy-leading-3' : 'sy-truncate']"
        >{{ title }}</span>
        <span
            v-else
            :style="textStyle"
            :class="[wrap ? 'sy-pre-wrap sy-leading-3' : 'sy-truncate']"
        >{{ title }}</span>
        <div v-if="!readonly" class="flex-container align-middle">
            <Icon icon="edit" class="sy-edit-field-icon" />
        </div>
    </div>
</template>
<style lang="scss" scoped>
    .ag-cell {
        &:hover {
            .sy-edit-field-icon {
                display: block;
            }
        }
    }
    .sy-edit-field {
        display: flex;
        height: 100%;
        padding: 0 var(--sy-spacer-8);
        align-items: center;
        justify-content: space-between;

        .sy-edit-field-icon {
            display: none;
        }

        &.editing {
            background-color: var(--sy-blue-100);
            color: var(--sy-blue-600);
        }
    }
</style>
